import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { gameServerHost } from "config/hosts";
import { getSessionParams, saveSessionParams } from "localapi";
import {
    login as httpLogin,
    loginDemo as httpLoginDemo,
    loginSid as httpLoginSid,
} from "http";
import queryParams from 'config/query';

export const logoutUser = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            await saveSessionParams(null);
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const loginUser = createAsyncThunk(
    'auth/login',
    async ({ login, password, rememberMe, backUrl, allowedHalls }, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { auth } = getState();
            const sessionParams = await httpLogin(login, password);
            if (allowedHalls && allowedHalls.indexOf(sessionParams.hallId) < 0) {
                throw new Error("login is not allowed for this hall");
            }
            console.log(`HTTP API login success`);
            socket.host = gameServerHost();
            socket.walletApiUsername = auth.walletApiUsername;
            await socket.connect();
            console.log(`Game Server connected`);
            const loginData = await socket.login(sessionParams);
            console.log(`Game Server login success`);
            if (rememberMe) {
                await saveSessionParams({
                    sid: sessionParams.sid,
                    key1: sessionParams.key1,
                    key2: sessionParams.key2,
                });
            }
            return { sessionParams, loginData, backUrl };
        } catch (err) {
            saveSessionParams(null);
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const loginUserDemo = createAsyncThunk(
    'auth/loginDemo',
    async ({ backUrl }, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { auth } = getState();
            const sessionParams = await httpLoginDemo();
            console.log(`HTTP API login success`);
            socket.host = gameServerHost();
            socket.walletApiUsername = auth.walletApiUsername;
            await socket.connect();
            console.log(`Game Server connected`);
            const loginData = await socket.login(sessionParams);
            console.log(`Game Server login success`);
            return { sessionParams, loginData, backUrl };
        } catch (err) {
            saveSessionParams(null);
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const loginUserAuto = createAsyncThunk(
    'auth/loginAuto',
    async (keys, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {

            const { auth } = getState();
            socket.host = gameServerHost();
            socket.walletApiUsername = auth.walletApiUsername;

            let params = await getSessionParams();
            if (!params) {
                const searchParams = (new URL(window.location.href)).searchParams;
                params = ['sid', 'key1', 'key2'].reduce((acc, curr) => {
                    return { ...acc, [curr]: searchParams.get(curr) };
                }, {});
                if (!params.sid || !params.key1 || !params.key2) {
                    throw new Error("no stored session params");
                }
            }

            const sessionParams = await httpLoginSid(params.sid, params.key1, params.key2);
            console.log(`HTTP API login success`);

            await socket.connect();
            console.log(`Game Server connected`);
            const loginData = await socket.login(sessionParams);
            console.log(`Game Server login success`);
            await saveSessionParams({
                sid: sessionParams.sid,
                key1: sessionParams.key1,
                key2: sessionParams.key2,
            });
            return { sessionParams, loginData };
        } catch (err) {
            saveSessionParams(null);
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const loginUserServerless = createAsyncThunk(
    'auth/loginUserServerless',
    async ({ sid, key1, key2, login, password, rememberMe }, { rejectWithValue }) => {
        try {
            const sessionParams = login === undefined ?
                await httpLoginSid(sid, key1, key2) :
                await httpLogin(login, password);
            console.log(`HTTP API login success`);
            if (rememberMe) {
                await saveSessionParams({
                    sid: sessionParams.sid,
                    key1: sessionParams.key1,
                    key2: sessionParams.key2,
                });
            }
            return { sessionParams: { ...sessionParams, serverLess: true } };
        } catch (err) {
            saveSessionParams(null);
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const restoreSession = createAsyncThunk(
    'auth/restoreSession',
    async (_, { extra, getState, rejectWithValue }) => {
        const { socket } = extra;
        try {
            const { auth } = getState();
            const { sid, key1, key2, walletApiUsername } = auth;

            const sessionParams = await httpLoginSid(sid, key1, key2);
            socket.host = gameServerHost();
            socket.walletApiUsername = walletApiUsername;
            await socket.connect();
            console.log("Transport connected");
            await socket.login(sessionParams);
            console.log("Logged in");
            return { sessionParams };
        } catch (err) {
            saveSessionParams(null);
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const closeTransport = createAsyncThunk(
    'auth/closeTransport',
    async (_, { extra, rejectWithValue }) => {
        const { socket } = extra;
        try {
            await socket.close();
            console.log("Transport disconnected");
        } catch (err) {
            return rejectWithValue(err.message || `error: ${err}`);
        }
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        sid: null,
        key1: null,
        key2: null,
        uid: null,
        hallId: null,
        isDemo: false,
        walletApiUsername: queryParams.walletApiUsername || queryParams.username || "",
        isLoginProgress: false,
        isLoginSuccess: false,
        loginError: false,
        isManualLogin: false,
        socket: null,
        chatLinks: null,
        chatsHidden: false,
        isConnected: false,
    },
    reducers: {
        clearState(state) {
            state.isLoginProgress = false;
            state.isLoginSuccess = false;
            state.loginError = false;
        },
        showChats(state, action) {
            state.chatsHidden = !action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loginUser.rejected, (state, { payload }) => {
            console.warn('loginUser rejected:', payload);
            state.isLoginProgress = false;
            state.loginError = payload;
            state.isConnected = false;
        });
        builder.addCase(loginUserAuto.rejected, (state, { payload }) => {
            console.warn('loginUserAuto rejected:', payload);
            state.isLoginProgress = false;
            state.isConnected = false;
        });
        builder.addCase(loginUserDemo.rejected, (state, { payload }) => {
            console.warn('loginUserDemo rejected:', payload);
            state.isLoginProgress = false;
            state.isConnected = false;
        });
        builder.addCase(loginUserServerless.rejected, (state, { payload }) => {
            console.warn('loginUserServerless rejected:', payload);
            state.isLoginProgress = false;
            state.isConnected = false;
        });
        builder.addCase(closeTransport.fulfilled, (state) => {
            state.isConnected = false;
        });
        builder.addMatcher(isAnyOf(loginUser.fulfilled, loginUserAuto.fulfilled, loginUserDemo.fulfilled, loginUserServerless.fulfilled), (state, { payload, type }) => {
            console.log('loginUser succeeded');
            const { sessionParams, backUrl } = payload;
            state.sid = sessionParams.sid;
            state.key1 = sessionParams.key1;
            state.key2 = sessionParams.key2;
            state.uid = sessionParams.uid;
            state.hallId = sessionParams.hallId;
            state.chatLinks = sessionParams.chatLinks || [];
            state.isLoginProgress = false;
            state.isLoginSuccess = true;
            state.backUrl = backUrl;
            state.serverLess = sessionParams.serverLess;
            state.isDemo = state.hallId === 7;
            state.isManualLogin = type === 'auth/login/fulfilled';
            state.isConnected = true;
            return state;
        });
        builder.addMatcher(isAnyOf(loginUser.pending, loginUserAuto.pending, loginUserDemo.pending, loginUserServerless.pending), (state) => {
            state.loginError = false;
            state.isLoginProgress = true;
            state.isConnected = false;
        });
        builder.addMatcher(isAnyOf(restoreSession.fulfilled), (state, { payload }) => {
            const { sessionParams } = payload;
            state.sid = sessionParams.sid;
            state.key1 = sessionParams.key1;
            state.key2 = sessionParams.key2;
            state.uid = sessionParams.uid;
            state.isLoginProgress = false;
            state.isLoginSuccess = true;
            state.isManualLogin = false;
            state.isConnected = true;
            return state;
        });

    },
});

export const authSelector = (state) => state.auth;
export const { clearState, showChats } = authSlice.actions;
export default authSlice.reducer;